import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink, Search } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { EVENTS, track } from "@/lib/analytics";
import { PROP_ODDS } from "@/constants/testIds";

const TITLE = "MLB Player Prop Odds Comparison | Parlay Assistant";
const DESCRIPTION =
  "Compare matching pregame MLB player prop odds from multiple sportsbooks. Review exact lines and find the best listed price in one place.";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// -- Formatters (display-only; no calculation changes) --------------------

const formatAmerican = (n) => (n > 0 ? `+${n}` : `${n}`);
const formatLine = (line, side) => {
  if (line === null || line === undefined) {
    return side === "yes" ? "Yes" : side === "no" ? "No" : "";
  }
  return `${line}`;
};
const sideLabel = (side) =>
  side === "over" ? "Over" : side === "under" ? "Under" : side === "yes" ? "Yes" : side === "no" ? "No" : side;

const formatET = (iso, opts = { includeDate: false }) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const base = { timeZone: "America/Detroit", hour: "numeric", minute: "2-digit", hour12: true };
    if (opts.includeDate) {
      base.weekday = "short";
      base.month = "short";
      base.day = "numeric";
    }
    return new Intl.DateTimeFormat("en-US", base).format(d) + " ET";
  } catch {
    return "—";
  }
};

const todayET = () => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Detroit",
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date());
  } catch {
    return "";
  }
};

// -- Category derivation from an already-loaded game ----------------------

// Returns [{ statID, statLabel, count }] for a game, in a stable order.
const gameCategories = (game) => {
  const seen = new Map();
  for (const pl of game.players || []) {
    for (const pr of pl.props || []) {
      const cur = seen.get(pr.statID);
      if (cur) cur.count += 1;
      else seen.set(pr.statID, { statID: pr.statID, statLabel: pr.statLabel, count: 1 });
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.statLabel.localeCompare(b.statLabel));
};

// -- Best-of-player summary for a category (uses best-priced prop row) ----

// Returns the "headline" prop for a player in a category: the row whose best
// price is the most attractive (highest positive; among negatives, closest to 0).
// This is purely a display choice; the underlying comparison is already computed
// per-row server-side.
const headlinePropForPlayer = (player, statID) => {
  const rows = (player.props || []).filter((p) => p.statID === statID);
  if (rows.length === 0) return null;
  const score = (p) => {
    const o = p.bestPrice;
    if (o === null || o === undefined) return -Infinity;
    return o > 0 ? 100 + o : -1000 / Math.abs(o);
  };
  return rows.slice().sort((a, b) => score(b) - score(a))[0];
};

export default function PlayerPropsPage() {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/player-props" });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [playerQuery, setPlayerQuery] = useState("");
  const [openGameId, setOpenGameId] = useState(null);
  const [selectedCatByGame, setSelectedCatByGame] = useState({}); // { [evId]: statID }
  const [openPlayerByGame, setOpenPlayerByGame] = useState({}); // { [evId]: playerID }

  // page-viewed event (once)
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    track(EVENTS.PROP_ODDS_PAGE_VIEWED, {});
  }, []);

  // Fetch once — server uses cached data; no provider hit for these clicks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await fetch(`${API_BASE}/api/prop-odds`, {
          headers: { Accept: "application/json" },
        });
        if (!r.ok) throw new Error(`server ${r.status}`);
        const body = await r.json();
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setError("We couldn't load prop odds right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const games = useMemo(() => data?.games || [], [data]);

  // Filter games by player search (case-insensitive substring on player name).
  const q = playerQuery.trim().toLowerCase();
  const filteredGames = useMemo(() => {
    if (!q) return games;
    return games
      .map((g) => {
        const matchingPlayers = (g.players || []).filter((pl) =>
          pl.playerName.toLowerCase().includes(q),
        );
        if (matchingPlayers.length === 0) return null;
        return { ...g, players: matchingPlayers };
      })
      .filter(Boolean);
  }, [games, q]);

  // Auto-open earliest upcoming game, or first matching game when searching.
  useEffect(() => {
    if (loading || filteredGames.length === 0) {
      setOpenGameId(null);
      return;
    }
    const earliest = filteredGames
      .slice()
      .sort((a, b) => (a.startsAt || "").localeCompare(b.startsAt || ""))[0];
    setOpenGameId((prev) => {
      // if a game is open AND still present, keep it. Otherwise open the earliest.
      if (prev && filteredGames.some((g) => g.eventID === prev)) return prev;
      return earliest.eventID;
    });
  }, [loading, filteredGames]);

  // Ensure a category is selected for the open game.
  useEffect(() => {
    if (!openGameId) return;
    const g = filteredGames.find((gg) => gg.eventID === openGameId);
    if (!g) return;
    setSelectedCatByGame((prev) => {
      if (prev[openGameId]) return prev;
      const cats = gameCategories(g);
      if (cats.length === 0) return prev;
      return { ...prev, [openGameId]: cats[0].statID };
    });
  }, [openGameId, filteredGames]);

  const toggleGame = (evId) => {
    setOpenGameId((prev) => {
      if (prev === evId) return null;
      track(EVENTS.PROP_GAME_EXPANDED, { event: evId });
      return evId;
    });
    // Close any expanded player rows when switching games.
    setOpenPlayerByGame((prev) => ({ ...prev, [evId]: null }));
  };

  const selectCategory = (evId, statID) => {
    setSelectedCatByGame((prev) => ({ ...prev, [evId]: statID }));
    setOpenPlayerByGame((prev) => ({ ...prev, [evId]: null }));
    track(EVENTS.PROP_CATEGORY_SELECTED, { category: statID });
  };

  const togglePlayer = (evId, pid) => {
    setOpenPlayerByGame((prev) => {
      const isOpen = prev[evId] === pid;
      if (!isOpen) track(EVENTS.PROP_PLAYER_EXPANDED, { player_present: true });
      return { ...prev, [evId]: isOpen ? null : pid };
    });
  };

  const searchTimerRef = useRef(null);
  const onSearchChange = (v) => {
    setPlayerQuery(v);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      // Privacy: only track the LENGTH, never the text.
      track(EVENTS.PROP_PLAYER_SEARCHED, { query_length: v.trim().length });
    }, 500);
  };

  return (
    <main data-testid={PROP_ODDS.page} className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-14">
      <div className="mb-6">
        <Link
          to="/"
          className="pa-nav-link inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to Calculators
        </Link>
      </div>

      {/* Intro */}
      <section className="pa-card p-6 md:p-8">
        <p className="font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
          Prop Odds · MLB
        </p>
        <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-slate-900 mt-1">
          MLB Player Prop Odds Comparison
        </h1>
        <p
          className="mt-2 text-sm text-slate-500 font-mono-num"
          data-testid={PROP_ODDS.updatedLabel}
        >
          Pregame odds • Updated periodically
          {data?.updated_at ? ` · last checked ${formatET(data.updated_at)}` : ""}
        </p>
        <p className="mt-3 text-slate-600 text-sm md:text-base max-w-3xl">
          Compare matching MLB player-prop lines from multiple sportsbooks. Prices are updated at
          scheduled times and are not live.
        </p>
        <p className="mt-2 text-xs text-slate-500" data-testid={PROP_ODDS.todayLabel}>
          Today ({todayET()})
        </p>

        {/* Stale / cutoff banners */}
        {data?.cutoff_reached && (
          <div
            data-testid={PROP_ODDS.cutoffBanner}
            className="mt-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm"
          >
            Data may be delayed. Always confirm the current line and price with the sportsbook.
          </div>
        )}
        {data?.stale && !data?.cutoff_reached && (
          <div
            data-testid={PROP_ODDS.staleBanner}
            className="mt-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 px-4 py-3 text-sm"
          >
            Data may be delayed. Always confirm the current line and price with the sportsbook.
          </div>
        )}

        {/* Player search — only remaining filter after category buttons replaced dropdowns */}
        <div className="mt-6">
          <label
            htmlFor="prop-odds-search"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
          >
            Player search
          </label>
          <div className="relative mt-2 max-w-md">
            <Search
              aria-hidden="true"
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              id="prop-odds-search"
              data-testid={PROP_ODDS.playerSearch}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              placeholder="Search a player…"
              value={playerQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pa-input"
              style={{ paddingLeft: "2rem" }}
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mt-8">
        {loading && (
          <div data-testid={PROP_ODDS.loading} className="pa-card p-6 text-slate-600 text-sm">
            Loading the latest pregame prop lines…
          </div>
        )}

        {!loading && error && (
          <div data-testid={PROP_ODDS.error} className="pa-card p-6 text-slate-700">
            {error} Please refresh the page in a few minutes.
          </div>
        )}

        {!loading && !error && q && filteredGames.length === 0 && (
          <div data-testid={PROP_ODDS.noResults} className="pa-card p-6 text-slate-700">
            No players match &ldquo;{playerQuery}&rdquo;. Try a different name.
          </div>
        )}

        {!loading && !error && !q && games.length === 0 && (
          <div data-testid={PROP_ODDS.empty} className="pa-card p-6 text-slate-700">
            Player props are not available yet. Sportsbooks often post them closer to game time.
            Please check again later.
          </div>
        )}

        {/* Level 1 — MLB Odds sport section */}
        {!loading && !error && filteredGames.length > 0 && (
          <div className="pa-card p-5 md:p-6">
            <h2
              data-testid={PROP_ODDS.sportHeading}
              className="font-display text-lg md:text-xl uppercase tracking-wide text-slate-900"
            >
              MLB Odds
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {filteredGames.length}{" "}
              {filteredGames.length === 1 ? "upcoming game" : "upcoming games"}
            </p>

            {/* Level 2 — Games accordion */}
            <div className="mt-4 space-y-3">
              {filteredGames.map((g) => {
                const isOpen = openGameId === g.eventID;
                const cats = gameCategories(g);
                const selectedCat = selectedCatByGame[g.eventID] || cats[0]?.statID;
                const catRows = (g.players || [])
                  .map((pl) => ({ pl, headline: headlinePropForPlayer(pl, selectedCat) }))
                  .filter((r) => r.headline);
                const openPid = openPlayerByGame[g.eventID] || null;

                return (
                  <div
                    key={g.eventID}
                    data-testid={PROP_ODDS.gameAccordion(g.eventID)}
                    className="rounded-2xl border border-slate-200 bg-white"
                  >
                    {/* Game toggle */}
                    <button
                      type="button"
                      data-testid={PROP_ODDS.gameToggle(g.eventID)}
                      aria-expanded={isOpen}
                      aria-controls={PROP_ODDS.gamePanel(g.eventID)}
                      onClick={() => toggleGame(g.eventID)}
                      className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 md:py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-2xl"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="font-display text-sm md:text-base uppercase tracking-wide text-slate-900 block truncate">
                          {g.away.name} <span className="text-slate-400">@</span> {g.home.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500 font-mono-num">
                          {formatET(g.startsAt)} • {cats.length}{" "}
                          {cats.length === 1 ? "prop category" : "prop categories"}
                        </span>
                      </span>
                      {isOpen ? (
                        <ChevronDown size={18} className="shrink-0 text-slate-500" />
                      ) : (
                        <ChevronRight size={18} className="shrink-0 text-slate-500" />
                      )}
                    </button>

                    {/* Level 3 + 4 + 5 — panel */}
                    {isOpen && (
                      <div
                        id={PROP_ODDS.gamePanel(g.eventID)}
                        className="px-4 md:px-5 pb-4 md:pb-5 pt-1 border-t border-slate-100"
                      >
                        {/* Category tabs */}
                        {cats.length > 0 && (
                          <div className="mt-3 -mx-1 overflow-x-auto">
                            <div className="flex gap-2 px-1 min-w-min">
                              {cats.map((c) => {
                                const active = c.statID === selectedCat;
                                return (
                                  <button
                                    key={c.statID}
                                    type="button"
                                    data-testid={PROP_ODDS.categoryTab(g.eventID, c.statID)}
                                    aria-pressed={active}
                                    onClick={() => selectCategory(g.eventID, c.statID)}
                                    className="pa-chip shrink-0"
                                    style={{
                                      background: active ? "#065f46" : undefined,
                                      color: active ? "#ecfdf5" : undefined,
                                      borderColor: active ? "#065f46" : undefined,
                                    }}
                                  >
                                    {c.statLabel}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Players list for this category */}
                        <div className="mt-4 space-y-2">
                          {catRows.length === 0 && (
                            <p className="text-sm text-slate-500">
                              No matching lines in this category.
                            </p>
                          )}
                          {catRows.map(({ pl, headline }) => {
                            const isPlayerOpen = openPid === pl.playerID;
                            const bestBooks = (headline.bestBookIndex || []).map(
                              (i) => headline.books[i]?.bookName,
                            );
                            return (
                              <div
                                key={pl.playerID}
                                data-testid={PROP_ODDS.playerRow(g.eventID, pl.playerID)}
                                className="rounded-xl border border-slate-200 bg-slate-50"
                              >
                                {/* Player toggle */}
                                <button
                                  type="button"
                                  data-testid={PROP_ODDS.playerToggle(g.eventID, pl.playerID)}
                                  aria-expanded={isPlayerOpen}
                                  aria-controls={PROP_ODDS.playerPanel(g.eventID, pl.playerID)}
                                  onClick={() => togglePlayer(g.eventID, pl.playerID)}
                                  className="w-full flex items-center justify-between gap-3 px-3 md:px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-xl"
                                >
                                  <span className="flex-1 min-w-0">
                                    <span className="font-display text-sm uppercase tracking-wide text-slate-900 block">
                                      {pl.playerName}{" "}
                                      <span className="text-slate-400 normal-case font-sans">
                                        — {sideLabel(headline.side)}
                                        {headline.line !== null && headline.line !== undefined ? (
                                          <>
                                            {" "}
                                            <span className="font-mono-num">
                                              {formatLine(headline.line, headline.side)}
                                            </span>
                                          </>
                                        ) : null}{" "}
                                        {headline.statLabel}
                                      </span>
                                    </span>
                                    <span className="mt-0.5 block text-xs text-slate-500">
                                      Best listed price:{" "}
                                      <span className="font-mono-num text-slate-800">
                                        {formatAmerican(headline.bestPrice)}
                                      </span>{" "}
                                      • {bestBooks.join(" and ") || "—"} •{" "}
                                      {headline.books.length} books compared
                                    </span>
                                  </span>
                                  {isPlayerOpen ? (
                                    <ChevronDown size={16} className="shrink-0 text-slate-500" />
                                  ) : (
                                    <ChevronRight size={16} className="shrink-0 text-slate-500" />
                                  )}
                                </button>

                                {/* Level 5 — Sportsbook prices */}
                                {isPlayerOpen && (
                                  <div
                                    id={PROP_ODDS.playerPanel(g.eventID, pl.playerID)}
                                    className="px-3 md:px-4 pb-3 pt-1 border-t border-slate-200"
                                  >
                                    <p className="mt-2 text-[11px] uppercase tracking-wider text-slate-400 font-mono-num">
                                      Last checked {formatET(headline.lastCheckedAt)}
                                    </p>
                                    <div className="mt-2 overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                            <th className="py-2 pr-3">Sportsbook</th>
                                            <th className="py-2 pr-3">American odds</th>
                                            <th className="py-2 pr-3">Best price</th>
                                            <th className="py-2 pr-3">Sportsbook link</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {headline.books.map((b, i) => {
                                            const isBest = (
                                              headline.bestBookIndex || []
                                            ).includes(i);
                                            return (
                                              <tr
                                                key={b.book}
                                                className="border-b border-slate-100"
                                                data-testid={PROP_ODDS.bookOdds(
                                                  g.eventID,
                                                  pl.playerID,
                                                  b.book,
                                                )}
                                              >
                                                <td className="py-2 pr-3">{b.bookName}</td>
                                                <td className="py-2 pr-3 font-mono-num text-slate-800">
                                                  {formatAmerican(b.odds)}
                                                </td>
                                                <td className="py-2 pr-3">
                                                  {isBest ? (
                                                    <span
                                                      data-testid={PROP_ODDS.bestBadge(
                                                        g.eventID,
                                                        pl.playerID,
                                                        b.book,
                                                      )}
                                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                                                      style={{
                                                        background: "#dcfce7",
                                                        color: "#047857",
                                                      }}
                                                    >
                                                      Best listed price
                                                    </span>
                                                  ) : (
                                                    <span className="text-slate-300">—</span>
                                                  )}
                                                </td>
                                                <td className="py-2 pr-3">
                                                  {b.deeplink ? (
                                                    <a
                                                      href={b.deeplink}
                                                      target="_blank"
                                                      rel="noopener noreferrer nofollow"
                                                      onClick={() =>
                                                        track(
                                                          EVENTS.SPORTSBOOK_LINK_CLICKED,
                                                          {
                                                            sportsbook: b.book,
                                                            prop_category: headline.statID,
                                                          },
                                                        )
                                                      }
                                                      data-testid={PROP_ODDS.deeplink(
                                                        g.eventID,
                                                        pl.playerID,
                                                        b.book,
                                                      )}
                                                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800"
                                                    >
                                                      Open <ExternalLink size={12} />
                                                    </a>
                                                  ) : (
                                                    <span className="text-slate-300">—</span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <p className="mt-8 text-xs text-slate-500 max-w-3xl">
        Odds are provided for informational comparison only and may change. Parlay Assistant does
        not provide picks or betting advice. Always confirm the current line and price with the
        sportsbook. Odds data via SportsGameOdds. FanDuel, DraftKings, BetMGM, Caesars and other
        sportsbook names are trademarks of their respective owners and do not sponsor or endorse
        Parlay Assistant.
      </p>
    </main>
  );
}
